import { ButtonV2, LabraToolTipV2 } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import {
  FlyoutOnboardingSectionName,
  LockMetadata,
  Sections,
} from '../../../../modules/flyOutOnboarding/types'
import { useDispatch, useSelector } from '../../../../store'
import {
  autoSave,
  isLoading as Loading,
  LoadingTypes,
  useAccountId,
  useTenantPartnerId,
} from '@labrav/common'
import {
  getFlyOutOnboardingData,
  sendFlyOutOnboardingData,
} from '../../../../modules/flyOutOnboarding/action'
import { isEmpty } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { CloudType } from '../../../../types'
import { autosaveTimeout } from '../../../../common/utils/constants'
import { footerStateType } from '../../../../common/components/FooterV2/FooterV2'
import { getPageMarkerMapping } from '../../../../common/utils/getPageMarkerMapping'
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { useStyles } from '../FlyOutAwsOnboarding.styles'
import { useFlyOutOnboardingStatus } from './useFlyOutOnboardingStatus'
import { checkTerms, navigateToNextPage } from './helpers'

export interface FlyOutOnboardingSaveButtonProps {
  sectionName: FlyoutOnboardingSectionName
  isFormLocked: boolean
  disableTooltipMsg: string
  lockStatus?: string
  latestLockMetadata: LockMetadata
  userProfileId: string
  setLockStatus: React.Dispatch<React.SetStateAction<string>>
}
export const FlyOutOnboardingSaveButton: React.FC<
  FlyOutOnboardingSaveButtonProps
> = ({
  sectionName,
  isFormLocked,
  disableTooltipMsg,
  lockStatus,
  setLockStatus,
  userProfileId,
  latestLockMetadata,
}) => {
  const { classes } = useStyles({})
  const navigate = useNavigate()
  const { isCompanyInfoComplete } = useFlyOutOnboardingStatus()
  const dispatch = useDispatch()
  const { values, errors, isValid, submitForm } =
    useFormikContext() as FormikProps<Sections>

  const { isLoading } = useSelector<{
    isLoading: boolean
  }>(state => ({
    isLoading: Loading(state.loading)(LoadingTypes.SUBMIT_FLYOUT_ONBOARDING),
  }))

  const accountId = useAccountId({ product: 'flyout', cloud: CloudType.AWS })
  const tenantPartnerId = useTenantPartnerId() ?? ''
  const hasFlyoutOnboardinData = useSelector<boolean>(
    state => !isEmpty(state.flyOutOnboarding.onboardingForm)
  )

  useEffect(() => {
    if (accountId && tenantPartnerId && !hasFlyoutOnboardinData) {
      dispatch(
        getFlyOutOnboardingData(accountId, tenantPartnerId, CloudType.AWS)
      )
    }
  }, [accountId, tenantPartnerId])

  const [counter, setCounter] = useState<number>(0)

  const callBackFlyoutOnboarding = () => {
    setLockStatus('')
  }
  const handleSubmitAPI = async (status?: string) => {
    if (accountId) {
      const isErrorInCurrentPage = !isEmpty(errors[sectionName])
      const currentPageMarker = pageMarkerMapping[sectionName]

      const updateOnboarding = {
        ...values,
        [FlyoutOnboardingSectionName.PAGE_MARKER]: {
          ...values[FlyoutOnboardingSectionName.PAGE_MARKER],
          [currentPageMarker]:
            !isErrorInCurrentPage && checkTerms(values, sectionName),
        },
      }
      const isLockEmpty =
        isEmpty(updateOnboarding?.lockMetadata?.lockedBy) ||
        isEmpty(updateOnboarding?.lockMetadata?.windowId)
      if (status === 'lockAcquire' || isLockEmpty) {
        // Use existing window.name or generate a new one if it doesn't exist
        if (!window.name) {
          window.name = uuidv4()
        }
        const newLastActivityAt = DateTime.now().toUTC().toISO()

        updateOnboarding.lockMetadata = {
          ...updateOnboarding.lockMetadata,
          windowId: window.name,
          lockedBy: userProfileId,
          lastActivityAt: newLastActivityAt,
          lockedByName: latestLockMetadata.lockedByName,
          lockByEmail: latestLockMetadata.lockByEmail,
          lockAcquisitionProbe: isLockEmpty ? undefined : true,
        }
      } else {
        updateOnboarding.lockMetadata = {
          ...updateOnboarding.lockMetadata,
          lockAcquisitionProbe: undefined,
        }
      }

      await dispatch(
        sendFlyOutOnboardingData({
          partnerId: accountId,
          tenantPartnerId,
          isAutoSave: false,
          onboardingForm: updateOnboarding,
          page: sectionName,
          pageMarkerMapping: pageMarkerMapping,
          callBackFlyoutOnboarding,
          status,
        })
      )
      const shouldNavigateToCompanyInformation = !isCompanyInfoComplete

      isEmpty(status) &&
        (shouldNavigateToCompanyInformation
          ? navigate('/flyout/onboarding/company-information')
          : navigateToNextPage(
              sectionName,
              navigate,
              updateOnboarding[
                FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
              ].isManaged
            ))
    }
  }

  const handleSubmit = () => {
    submitForm()
    const isPageValid =
      sectionName === FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
        ? isEmpty(errors[sectionName]) &&
          isEmpty(errors[FlyoutOnboardingSectionName.LANDING_PAGE_DETAILS])
        : isEmpty(errors[sectionName])
    const shouldCallAPI =
      sectionName === FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
        ? isPageValid &&
          checkTerms(values, FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS)
        : isPageValid
    if (shouldCallAPI) {
      handleSubmitAPI()
    }
  }

  const pageMarkerMapping = getPageMarkerMapping()

  const autoSaving = async () => {
    if (accountId) {
      const isErrorInCurrentPage = !isEmpty(errors[sectionName]) || !isValid
      const currentPageMarker = pageMarkerMapping[sectionName]
      const markerChanges = {
        [currentPageMarker]:
          !isErrorInCurrentPage && checkTerms(values, sectionName),
      }
      const updateOnboarding = {
        ...values,
        [FlyoutOnboardingSectionName.PAGE_MARKER]: {
          ...values[FlyoutOnboardingSectionName.PAGE_MARKER],
          ...(currentPageMarker ? markerChanges : {}),
        },
        lockMetadata: {
          ...values.lockMetadata,
          windowId: latestLockMetadata.windowId,
          lockedBy: latestLockMetadata.lockedBy,
          lockedByName: latestLockMetadata.lockedByName,
          lockByEmail: latestLockMetadata.lockByEmail,
          lockAcquisitionProbe: isFormLocked ? true : undefined,
        },
      }

      await dispatch(
        autoSave('flyOutOnboarding', async () => {
          return await dispatch(
            sendFlyOutOnboardingData({
              partnerId: accountId,
              tenantPartnerId,
              isAutoSave: true,
              onboardingForm: updateOnboarding,
              pageMarkerMapping,
              page: sectionName,
            })
          )
        })
      )
    }
  }
  useEffect(() => {
    const interval = setInterval(() => {
      setCounter(counter + 1)
    }, autosaveTimeout)
    return () => clearInterval(interval)
  })
  useEffect(() => {
    if (counter > 0) {
      autoSaving()
    }
  }, [counter])

  return (
    <>
      {lockStatus !== footerStateType.UNLOCKED ? (
        <>
          {sectionName ===
            FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS && (
            <LabraToolTipV2
              tooltipPlacement="top-end"
              description={disableTooltipMsg}
              tooltipPlacmentGap="32px"
            >
              <div className={isFormLocked ? classes.disableBtn : ''}>
                <ButtonV2
                  styleType="outlined"
                  data-testid={'Back-button'}
                  onClick={() => navigate(-1)}
                  disabled={isFormLocked}
                >
                  Back
                </ButtonV2>
              </div>
            </LabraToolTipV2>
          )}
          <LabraToolTipV2
            tooltipPlacement="top-end"
            description={disableTooltipMsg}
            tooltipPlacmentGap="32px"
          >
            <div className={isFormLocked ? classes.disableBtn : ''}>
              <ButtonV2
                data-testid="save-button-flyout-onboarding"
                styleType="primary"
                onClick={handleSubmit}
                disabled={isFormLocked}
                loading={isLoading}
              >
                Submit
              </ButtonV2>
            </div>
          </LabraToolTipV2>
        </>
      ) : (
        <>
          <ButtonV2
            data-testid="save-button-flyout-onboarding-put"
            styleType="primary"
            onClick={(event: any) => handleSubmitAPI('lockAcquire')}
            loading={isLoading}
          >
            Start editing
          </ButtonV2>
        </>
      )}
    </>
  )
}
